import { type ReactNode } from 'react';

import { Box, type SxProps, type Theme } from '@mui/material';

// The content area of a shell: a flex column that fills the space beside the
// sidebar and holds the active view, with the standard gutter. Its child owns
// the scrolling (the area itself stays put). A reusable layout shell. Pass `sx`
// to layer on responsive behaviour (e.g. a tighter gutter at narrow widths).
//
// Content caps at 1200 and centres, per the CAM Backend layout in Figma. The
// outer box keeps the gutter and the overflow so a wide monitor gets more
// margin rather than wider tables: past about 1200 a row's cells drift so far
// apart that reading across one stops working, and the eye has nothing to
// travel along.
const CONTENT_MAX = 1200;

const Layout = ({
  children,
  sx,
}: {
  children: ReactNode;
  sx?: SxProps<Theme>;
}) => (
  <Box
    sx={[
      {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 3,
      },
      // An incoming sx may itself be an array, and array members can't be
      // undefined, so flatten and default before spreading.
      ...(Array.isArray(sx) ? sx : [sx ?? {}]),
    ]}
  >
    {/* The measure. Repeats the parent's flex-column and overflow so children
        that scroll themselves (flex={1} minHeight={0} overflow='auto') still
        find a bounded column to do it in. */}
    <Box
      sx={{
        mx: 'auto',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        maxWidth: CONTENT_MAX,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  </Box>
);

export default Layout;
